import React, { useMemo, useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { validateDateRangeTaipei } from '../utils/dateParse';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { countBusinessDays } from '../utils/dateUtils';

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getDaysInMonth = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= totalDays; d++) days.push(new Date(year, month, d));
  return days;
};

const isoTaipei = (date) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit'
}).format(date);
const mdyTaipei = (date) => new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit'
}).format(date).replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$1-$2-$3');
const withinRange = (date, startISO, endISO) => {
  if (!date) return false;
  if (!startISO || !endISO) return true;
  const d = isoTaipei(date);
  return d >= startISO && d <= endISO;
};

const CalendarViewModal = ({
  open,
  onOpenChange,
  month,
  year,
  onMonthYearChange,
  attendanceRecords,
  startDate,
  endDate,
  workweek = [1, 2, 3, 4, 5],
  onRefresh,
  loading,
  onFetchDateDetails,
  onApplyDateFilter,
  onClearDateFilter,
  presentDates = [],
  halfDates = [],
  absentDates = [],
  holidayDates = []
}) => {
  const gridRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [dayDetails, setDayDetails] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState('');

  const normalizeType = (t) => {
    if (!t) return '';
    const s = String(t).toLowerCase().trim();
    if (s === 'time_in' || s === 'time in' || s === 'in') return 'time_in';
    if (s === 'time_out' || s === 'time out' || s === 'out') return 'time_out';
    return s.replace(/\s+/g, '_');
  };

  useEffect(() => {
    setSelectedDate(null);
  }, [month, year, startDate, endDate]);

  const dailyByDate = useMemo(() => {
    const parse = (ts) => {
      if (!ts) return null;
      if (typeof ts === 'string') {
        const trimmed = ts.trim();
        if (!trimmed) return null;
        if (trimmed.includes('+') || trimmed.endsWith('Z')) return new Date(trimmed);
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return new Date(trimmed + 'T00:00:00+08:00');
        if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?$/.test(trimmed)) {
          const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
          const withSeconds = /:\d{2}:\d{2}/.test(iso) ? iso : iso.replace(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?!:)/, '$1:00');
          return new Date(withSeconds + '+08:00');
        }
        return new Date(trimmed);
      }
      return new Date(ts);
    };

    const m = new Map();
    (attendanceRecords || []).forEach((r) => {
      try {
        const d = parse(r?.timestamp);
        if (!d || isNaN(d)) return;
        const dateISO = isoTaipei(d);
        const t = normalizeType(r?.type);
        const cur = m.get(dateISO) || { in: false, out: false };
        if (t === 'time_in') cur.in = true;
        else if (t === 'time_out') cur.out = true;
        m.set(dateISO, cur);
      } catch {}
    });
    return m;
  }, [attendanceRecords]);
  const presentSet = useMemo(() => new Set(presentDates || []), [presentDates]);
  const halfSet = useMemo(() => new Set(halfDates || []), [halfDates]);
  const absentSet = useMemo(() => new Set(absentDates || []), [absentDates]);
  const holidaySet = useMemo(() => new Set(holidayDates || []), [holidayDates]);

  const days = useMemo(() => getDaysInMonth(year, month), [year, month]);
  const businessDaysCount = useMemo(() => {
    if (!startDate || !endDate) {
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      return countBusinessDays(isoTaipei(first), isoTaipei(last), workweek);
    }
    return countBusinessDays(startDate, endDate, workweek);
  }, [startDate, endDate, workweek, year, month]);
  useEffect(() => {
    if (!startDate || !endDate) return;
    const res = validateDateRangeTaipei(startDate, endDate);
    if (!res.ok) {
      try { toast.error('Invalid date range format. Required: MM-DD-YYYY. ' + res.errors.join('; ')); } catch {}
    }
  }, [startDate, endDate]);
  const loadedDatesCount = useMemo(() => {
    const s = new Set();
    (attendanceRecords || []).forEach(r => {
      const ts = r?.timestamp;
      if (!ts) return;
      let d;
      if (typeof ts === 'string') {
        const trimmed = ts.trim();
        if (trimmed.includes('+') || trimmed.endsWith('Z')) d = new Date(trimmed);
        else if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(trimmed)) {
          const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
          d = new Date(iso + '+08:00');
        } else {
          d = new Date(trimmed + 'Z');
        }
      } else {
        d = new Date(ts);
      }
      const iso = isoTaipei(d);
      if (!startDate || !endDate) {
        const monthMatches = d.getMonth() === month && d.getFullYear() === year;
        if (monthMatches) s.add(iso);
      } else {
        if (iso >= startDate && iso <= endDate) s.add(iso);
      }
    });
    return s.size;
  }, [attendanceRecords, startDate, endDate, month, year]);

  const handleKeyNav = (e) => {
    const idxs = days.map((d, i) => ({ d, i })).filter(x => !!x.d && withinRange(x.d, startDate, endDate)).map(x => x.i);
    if (idxs.length === 0) return;
    const currentIndex = selectedDate ? days.findIndex(d => d && isoTaipei(d) === isoTaipei(selectedDate)) : idxs[0];
    let nextIndex = currentIndex;
    if (e.key === 'ArrowRight') nextIndex = Math.min(days.length - 1, currentIndex + 1);
    if (e.key === 'ArrowLeft') nextIndex = Math.max(0, currentIndex - 1);
    if (e.key === 'ArrowDown') nextIndex = Math.min(days.length - 1, currentIndex + 7);
    if (e.key === 'ArrowUp') nextIndex = Math.max(0, currentIndex - 7);
    const nextDate = days[nextIndex];
    if (nextDate) setSelectedDate(nextDate);
  };

  const openDayDetails = async (date) => {
    try {
      setIsDayModalOpen(true);
      setDayLoading(true);
      setDayError('');
      setDayDetails([]);
      const dateISO = isoTaipei(date);
      if (onFetchDateDetails) {
        const res = await onFetchDateDetails(dateISO);
        setDayDetails(Array.isArray(res) ? res : []);
      } else {
        const list = (attendanceRecords || []).filter(r => {
          const ts = r?.timestamp; if (!ts) return false;
          let d;
          try {
            if (typeof ts === 'string') {
              const trimmed = ts.trim();
              if (trimmed.includes('+') || trimmed.endsWith('Z')) d = new Date(trimmed);
              else if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?$/.test(trimmed)) {
                const iso0 = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
                const iso = /:\d{2}:\d{2}/.test(iso0) ? iso0 : iso0.replace(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?!:)/, '$1:00');
                d = new Date(iso + '+08:00');
              } else {
                d = new Date(trimmed);
              }
            } else {
              d = new Date(ts);
            }
          } catch {
            d = new Date(ts);
          }
          return isoTaipei(d) === dateISO;
        });
        setDayDetails(list);
      }
    } catch (err) {
      setDayError('Failed to load date details');
    } finally {
      setDayLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] w-[95vw] max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Calendar View</DialogTitle>
          <DialogDescription>
            {startDate} to {endDate} • Business days: {businessDaysCount} • Loaded dates: {loadedDatesCount}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between mb-3" aria-label="Calendar navigation">
          <div className="flex gap-2">
            <Select value={String(month)} onValueChange={(v) => onMonthYearChange({ month: Number(v), year })}>
              <SelectTrigger className="w-[160px]" aria-label="Select month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }).map((_, m) => (
                  <SelectItem key={m} value={String(m)}>{new Date(2000, m, 1).toLocaleString('en-US', { month: 'long' })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => onMonthYearChange({ month, year: Number(v) })}>
              <SelectTrigger className="w-[120px]" aria-label="Select year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 11 }).map((_, idx) => {
                  const y = new Date().getFullYear() - 5 + idx;
                  return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onMonthYearChange({ month: new Date().getMonth(), year: new Date().getFullYear() })} aria-label="Jump to current month">Today</Button>
            {onRefresh && (
              <Button onClick={() => onRefresh()} aria-label="Refresh attendance" className="bg-indigo-600 hover:bg-indigo-700 text-white">Refresh</Button>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-6" aria-live="polite">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-3"></div>
            <span className="text-sm text-gray-700">Loading attendance dates…</span>
          </div>
        )}

        <div
          ref={gridRef}
          role="grid"
          aria-label="Attendance calendar"
          tabIndex={0}
          onKeyDown={handleKeyNav}
          className="w-full"
        >
          <div className="grid grid-cols-7 gap-1 text-xs text-gray-600 mb-1">
            {daysOfWeek.map(d => (
              <div key={d} className="text-center font-medium" aria-hidden>{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, idx) => {
              if (!date) return <div key={`empty-${idx}`} className="h-20 rounded" aria-hidden />;
              const dateISO = isoTaipei(date);
              const inRange = withinRange(date, startDate, endDate);
              const weekend = !workweek.includes(date.getDay());
              const isHoliday = inRange && holidaySet.has(dateISO);
              const drec = dailyByDate.get(dateISO) || { in: false, out: false };
              const isSystemPresentBase = inRange && !weekend && presentSet.has(dateISO);
              const isSystemHalf = inRange && !weekend && halfSet.has(dateISO);
              const rawFull = inRange && !weekend && drec.in && drec.out;
              const rawHalf = inRange && !weekend && (drec.in || drec.out) && (drec.in !== drec.out);
              const isSaturday = inRange && !weekend && date.getDay() === 6 && workweek.includes(6);
              const satInferredPresent = (() => {
                if (!isSaturday) return false;
                if (isHoliday) return false;
                if (isSystemPresentBase || isSystemHalf || drec.in || drec.out) return false;
                const hasPresence = (iso) => {
                  if (presentSet.has(iso) || halfSet.has(iso)) return true;
                  const r = dailyByDate.get(iso);
                  return !!(r && (r.in || r.out));
                };
                const prev = new Date(date);
                prev.setDate(prev.getDate() - 1);
                let guard = 0;
                while (guard < 10 && !workweek.includes(prev.getDay())) { prev.setDate(prev.getDate() - 1); guard++; }
                const next = new Date(date);
                next.setDate(next.getDate() + 1);
                guard = 0;
                while (guard < 10 && !workweek.includes(next.getDay())) { next.setDate(next.getDate() + 1); guard++; }
                const prevISO = isoTaipei(prev);
                const nextISO = isoTaipei(next);
                return hasPresence(prevISO) && hasPresence(nextISO);
              })();
              const isSystemPresent = isSystemPresentBase || satInferredPresent;
              const isFullPresent = inRange && !weekend && (isSystemPresent || (rawFull && !isSystemHalf));
              const isHalfPresent = inRange && !weekend && !isSystemPresent && (isSystemHalf || rawHalf);
              const isAbsent = inRange && !weekend && !isHoliday && !isFullPresent && !isHalfPresent;
              const isHolidayAbsent = inRange && !weekend && isHoliday && !isFullPresent && !isHalfPresent && absentSet.has(dateISO);
              const bg = !inRange ? 'bg-white' : (isHoliday ? 'bg-blue-200' : (weekend ? 'bg-gray-100' : (isHalfPresent ? 'bg-yellow-200' : isFullPresent ? 'bg-green-200' : isAbsent ? 'bg-red-200' : 'bg-white')));
              const color = weekend ? 'text-gray-400' : inRange ? 'text-black' : 'text-gray-500';
              const isSelected = selectedDate && isoTaipei(selectedDate) === dateISO;
              const ring = isSelected ? 'ring-2 ring-indigo-400' : '';
              if (!inRange) return <div key={dateISO} className="h-20 rounded" aria-hidden />;
              return (
                <button
                  key={dateISO}
                  role="gridcell"
                  aria-label={`Date ${date.toLocaleDateString()}`}
                  aria-selected={isSelected}
                  aria-disabled={weekend}
                  onClick={() => { setSelectedDate(date); openDayDetails(date); }}
                  className={`h-20 rounded border border-gray-200 flex flex-col items-center justify-center ${bg} ${color} ${ring} transition-colors`}
                >
                  <div className="text-sm font-medium">{date.getDate()}</div>
                  <div className="text-[10px]">
                    {weekend ? 'Weekend' : isHoliday ? (isHolidayAbsent ? 'Holiday (Absent)' : 'Holiday') : isHalfPresent ? 'Half Day' : isFullPresent ? 'Present' : isAbsent ? 'Absent' : ''}
                  </div>
                </button>
              );
            })}
          {/* Day Details Modal */}
          <Dialog open={isDayModalOpen} onOpenChange={setIsDayModalOpen}>
            <DialogContent className="sm:max-w-[700px]" aria-label="Employee details for selected date">
              <DialogHeader>
                <DialogTitle>Details • {selectedDate ? mdyTaipei(selectedDate) : ''}</DialogTitle>
                <DialogDescription>Schedule and activities for the selected date</DialogDescription>
              </DialogHeader>
              <button
                aria-label="Close"
                onClick={() => setIsDayModalOpen(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
              {dayLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-3"></div>
                  <span className="text-sm text-gray-700">Loading...</span>
                </div>
              ) : dayError ? (
                <div className="text-sm text-red-600">{dayError}</div>
              ) : (Array.isArray(dayDetails) && dayDetails.length > 0 ? (
                <div className="space-y-3">
                  {selectedDate && holidaySet.has(isoTaipei(selectedDate)) && (
                    <div className="border rounded p-3 bg-blue-50">
                      <div className="text-sm font-semibold text-gray-900">Holiday</div>
                      <div className="text-xs text-gray-600">This date is marked as a holiday.</div>
                    </div>
                  )}
                  {dayDetails.map((item, idx) => (
                    <div key={idx} className="border rounded p-3">
                      <div className="flex justify-between">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{item.user_name || item.name || 'Employee'}</div>
                          <div className="text-xs text-gray-600">{item.position || 'N/A'} • {item.department || item.branch || 'N/A'}</div>
                        </div>
                        <div className="text-right text-xs text-gray-600">
                          <div>Working hours: {(item.schedule_start || 'N/A')} – {(item.schedule_end || 'N/A')}</div>
                          {item.time_off && (<div>Time-off: {item.time_off}</div>)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                        <div><span className="text-gray-500">First Time In:</span> <span className="font-medium">{item.first_time_in || item.time_in || '—'}</span></div>
                        <div><span className="text-gray-500">Last Time Out:</span> <span className="font-medium">{item.last_time_out || item.time_out || '—'}</span></div>
                        <div className="col-span-2"><span className="text-gray-500">Events:</span> <span className="font-medium">{(item.events && item.events.length > 0) ? item.events.join(', ') : 'No scheduled events'}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedDate && holidaySet.has(isoTaipei(selectedDate)) ? (
                    <div className="border rounded p-3 bg-blue-50">
                      <div className="text-sm font-semibold text-gray-900">Holiday</div>
                      <div className="text-xs text-gray-600">This date is marked as a holiday.</div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">No data exists for the selected date.</div>
                  )}
                </div>
              ))}
              <div className="flex justify-end gap-2 mt-4">
                {onClearDateFilter && (
                  <Button variant="outline" onClick={() => onClearDateFilter()} aria-label="Clear date filter">Clear Filter</Button>
                )}
                {onApplyDateFilter && (
                  <Button onClick={() => selectedDate && onApplyDateFilter(isoTaipei(selectedDate))} className="bg-indigo-600 hover:bg-indigo-700 text-white" aria-label="Apply date filter">Filter by this date</Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarViewModal;
